import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { projectColor, fmtDate, tlPos, barSpan, STATE_LABEL } from "./timeline";

const COL_W = 150;
const FIRST_W = 290;

function todayPosOf(months, today) {
  const N = months.length;
  return today >= months[0].start && today <= months[N - 1].end ? (tlPos(today, months) / N) * 100 : null;
}

function MonthHead({ months, curKey }) {
  return (
    <thead>
      <tr>
        <th className="!bg-white sticky left-0 z-10 border-r border-slate-100">{months._label || "Proiect / Persoană alocată"}</th>
        {months.map((mo) => (
          <th key={mo.key} className={`text-center ${mo.key === curKey ? "!text-brand" : ""}`}>
            <div className="capitalize">{mo.short}</div>
            <div className="font-normal normal-case tracking-normal text-[10px] text-slate-400">{mo.year}</div>
          </th>
        ))}
      </tr>
    </thead>
  );
}

// ======================================================= PER PROIECT
export function ProjectTimeline({ rows, months, today, horizonEnd }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const N = months.length;
  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const todayPos = todayPosOf(months, today);
  const curKey = today.slice(0, 7);

  return (
    <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto" data-testid="project-timeline">
      <table className="pio-table" style={{ tableLayout: "fixed", minWidth: FIRST_W + N * COL_W }}>
        <colgroup>
          <col style={{ width: FIRST_W }} />
          {months.map((m) => <col key={m.key} style={{ width: COL_W }} />)}
        </colgroup>
        <MonthHead months={months} curKey={curKey} />
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={N + 1} className="py-10 text-center text-slate-400">Niciun proiect.</td></tr>
          )}
          {rows.map((r) => (
            <ProjectGroup key={r.project.id} row={r} months={months} today={today} todayPos={todayPos}
              horizonEnd={horizonEnd} open={expanded.has(r.project.id)} onToggle={() => toggle(r.project.id)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProjectGroup({ row, months, today, todayPos, horizonEnd, open, onToggle }) {
  const { project: p, members } = row;
  const N = months.length;
  const color = projectColor(p.id);
  const hasPeriod = !!(p.start_date || p.end_date);
  const projSpan = hasPeriod ? barSpan({ start_date: p.start_date, end_date: p.end_date }, months, today, horizonEnd) : null;
  return (
    <>
      <tr className="!bg-slate-50/80" data-testid={`tl-project-${p.id}`}>
        <td className="sticky left-0 z-[1] !bg-slate-50 border-r border-slate-100">
          <button onClick={onToggle} className="flex items-center gap-2 text-left w-full" data-testid={`tl-toggle-${p.id}`}>
            {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />}
            <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: color }} />
            <div className="min-w-0">
              <div className="font-mono text-[10px] text-slate-400 leading-tight">{p.code}{p.status === "completed" ? " · finalizat" : ""}</div>
              <div className="font-semibold text-slate-800 text-[13px] leading-tight truncate">{p.name}</div>
              <div className="text-[10px] text-slate-400 leading-tight">
                {members.length === 0 ? "fără resurse" : `${members.length} ${members.length === 1 ? "persoană" : "persoane"}`}
              </div>
            </div>
          </button>
        </td>
        <td colSpan={N} className="!p-0 relative !bg-slate-50/80">
          <Track N={N} todayPos={todayPos}>
            {projSpan && !projSpan.out ? (
              <div data-testid={`tl-project-bar-${p.id}`}
                className="absolute top-1.5 h-6 rounded-md text-white text-[10.5px] font-medium flex items-center px-2 overflow-hidden whitespace-nowrap shadow-sm"
                style={{ left: `${projSpan.left}%`, width: `${projSpan.width}%`, background: color, opacity: projSpan.openEnd ? 0.9 : 1 }}
                title={`${p.name}: ${fmtDate(p.start_date) || "…"} → ${fmtDate(p.end_date) || "fără termen"}`}>
                <span className="truncate">{fmtDate(p.start_date) || "…"} → {fmtDate(p.end_date) || "fără termen"}</span>
              </div>
            ) : (
              <div className="absolute inset-y-0 left-3 flex items-center text-[11px] text-slate-400 italic">
                {members.length === 0 ? "fără resurse alocate" : "proiect fără perioadă definită"}
              </div>
            )}
          </Track>
        </td>
      </tr>
      {open && members.map((m) => (
        <MemberRow key={m.assignment_id} m={m} months={months} today={today} todayPos={todayPos} horizonEnd={horizonEnd} color={color} />
      ))}
      {open && members.length === 0 && (
        <tr><td colSpan={N + 1} className="!py-2 pl-9 text-[11px] text-slate-400 border-b border-slate-100">Fără resurse alocate pe acest proiect.</td></tr>
      )}
    </>
  );
}

function MemberRow({ m, months, today, todayPos, horizonEnd, color }) {
  const N = months.length;
  const span = barSpan(m, months, today, horizonEnd);
  const endTxt = m.permanent ? "permanent" : (m.end_date ? `până la ${fmtDate(m.end_date)}` : "fără termen");
  const endCls = span.state === "ending" ? "text-amber-600 font-medium"
    : span.state === "ended" ? "text-slate-400 line-through" : "text-slate-500";
  return (
    <tr data-testid={`tl-member-${m.assignment_id}`}>
      <td className="!bg-white sticky left-0 z-[1] border-r border-slate-100 !py-1.5">
        <div className="pl-7">
          <div className="text-[13px] font-medium text-slate-800 leading-tight">{m.name}</div>
          <div className="text-[11px] leading-tight flex gap-1.5 items-center min-w-0">
            <span className="text-slate-400 truncate">{m.job_title || "—"}</span>
            {m.allocated_hours != null && <span className="text-brand shrink-0">· {m.allocated_hours}%</span>}
            <span className={`shrink-0 ${endCls}`}>· {endTxt}</span>
          </div>
        </div>
      </td>
      <td colSpan={N} className="!p-0 relative">
        <Track N={N} todayPos={todayPos}>
          {span.out ? (
            <div className="absolute inset-y-0 left-3 flex items-center text-[11px] text-slate-400 italic">{span.reason}</div>
          ) : (
            <Bar span={span} m={m} color={color} />
          )}
        </Track>
      </td>
    </tr>
  );
}

export function Track({ N, todayPos, children, className = "h-9", style }) {
  return (
    <div className={`relative ${className}`} style={style}>
      {Array.from({ length: N }).map((_, i) => (
        <div key={i} className="absolute inset-y-0 border-l border-slate-100" style={{ left: `${(i / N) * 100}%` }} />
      ))}
      {todayPos != null && (
        <div className="absolute inset-y-0 w-px bg-rose-400 z-[2]" style={{ left: `${todayPos}%` }} title="Azi" />
      )}
      {children}
    </div>
  );
}

const STATE_STYLE = {
  ending: { bg: "#d97706" },
  ended: { bg: "#94a3b8" },
  future: { dashed: true },
  permanent: { dashed: true, opacity: 0.85 },
  active: {},
};

function Bar({ span, m, color }) {
  const st = STATE_STYLE[span.state] || {};
  const label = span.state === "permanent"
    ? "alocare permanentă"
    : `${fmtDate(m.start_date) || "…"} → ${fmtDate(m.end_date) || "fără termen"}`;
  const radius = (open) => (open ? 0 : undefined);
  return (
    <div
      data-testid={`tl-bar-${m.assignment_id}`}
      className="absolute top-1.5 h-6 rounded-md text-white text-[10.5px] font-medium flex items-center px-2 overflow-hidden whitespace-nowrap shadow-sm"
      style={{
        left: `${span.left}%`, width: `${span.width}%`, background: st.bg || color, opacity: st.opacity ?? 1,
        border: st.dashed ? "1px dashed rgba(255,255,255,.75)" : "none",
        borderTopRightRadius: radius(span.openEnd), borderBottomRightRadius: radius(span.openEnd),
        borderTopLeftRadius: radius(span.openStart), borderBottomLeftRadius: radius(span.openStart),
      }}
      title={`${m.name}: ${label} · ${STATE_LABEL[span.state]}${m.allocated_hours != null ? ` · ${m.allocated_hours}%` : ""}`}
    >
      {span.state === "ending" && <AlertTriangle className="h-3 w-3 mr-1 shrink-0" />}
      <span className="truncate">{label}</span>
    </div>
  );
}

// ======================================================= PER ANGAJAT
export function EmployeeTimeline({ rows, months, today, horizonEnd }) {
  const [expanded, setExpanded] = useState(() => new Set());
  const N = months.length;
  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const todayPos = todayPosOf(months, today);
  const curKey = today.slice(0, 7);
  const head = [...months]; head._label = "Angajat / Proiect";

  return (
    <div className="overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto" data-testid="employee-timeline">
      <table className="pio-table" style={{ tableLayout: "fixed", minWidth: FIRST_W + N * COL_W }}>
        <colgroup>
          <col style={{ width: FIRST_W }} />
          {months.map((m) => <col key={m.key} style={{ width: COL_W }} />)}
        </colgroup>
        <MonthHead months={head} curKey={curKey} />
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={N + 1} className="py-10 text-center text-slate-400">Niciun angajat cu alocare.</td></tr>
          )}
          {rows.map((r) => (
            <EmployeeGroup key={r.employee.id} row={r} months={months} today={today} todayPos={todayPos}
              horizonEnd={horizonEnd} open={expanded.has(r.employee.id)} onToggle={() => toggle(r.employee.id)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmployeeGroup({ row, months, today, todayPos, horizonEnd, open, onToggle }) {
  const { employee: e, allocations = [] } = row;
  const N = months.length;
  const rowH = allocations.length ? allocations.length * 22 + 8 : 36;
  return (
    <>
      <tr className="!bg-slate-50/80" data-testid={`etl-emp-${e.id}`}>
        <td className="sticky left-0 z-[1] !bg-slate-50 border-r border-slate-100 align-top !py-2">
          <button onClick={onToggle} className="flex items-start gap-2 text-left w-full" data-testid={`etl-toggle-${e.id}`}>
            {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <div className="font-semibold text-slate-800 text-[13px] leading-tight truncate">{e.name}</div>
              <div className="font-mono text-[10px] text-slate-400 leading-tight truncate">{e.job_title || "—"}</div>
              <div className="text-[10px] leading-tight mt-0.5">
                <span className="text-brand">{row.alloc_pct ?? 0}% alocat</span>
                <span className={(row.free_pct ?? 100) > 0 ? "text-emerald-600" : "text-red-500"}> · {row.free_pct ?? 100}% liber</span>
              </div>
            </div>
          </button>
        </td>
        <td colSpan={N} className="!p-0 relative !bg-slate-50/80 align-top">
          {allocations.length === 0 ? (
            <div className="h-9 flex items-center pl-3">
              <span className="pio-badge pio-badge-red">Fără alocare cu perioadă</span>
            </div>
          ) : (
            <Track N={N} todayPos={todayPos} className="" style={{ height: rowH }}>
              {allocations.map((a, idx) => {
                const span = barSpan(a, months, today, horizonEnd);
                if (span.out) return null;
                const base = projectColor(a.project_id);
                const bg = span.state === "ending" ? "#d97706" : span.state === "ended" ? "#94a3b8" : base;
                const dashed = span.state === "future" || span.state === "permanent";
                return (
                  <div key={a.assignment_id} data-testid={`etl-ov-${a.assignment_id}`}
                    className="absolute h-4 rounded text-white text-[9.5px] font-medium flex items-center px-1.5 overflow-hidden whitespace-nowrap shadow-sm"
                    style={{
                      top: idx * 22 + 6, left: `${span.left}%`, width: `${span.width}%`,
                      background: bg, opacity: span.state === "permanent" ? 0.85 : 1,
                      border: dashed ? "1px dashed rgba(255,255,255,.75)" : "none",
                    }}
                    title={`${a.name}: ${fmtDate(a.start_date) || "…"} → ${fmtDate(a.end_date) || "fără termen"} · ${a.pct}%`}>
                    <span className="truncate">{a.code} · {a.pct}%</span>
                  </div>
                );
              })}
            </Track>
          )}
        </td>
      </tr>
      {open && allocations.map((a) => (
        <AllocRow key={a.assignment_id} a={a} months={months} today={today} todayPos={todayPos} horizonEnd={horizonEnd} />
      ))}
    </>
  );
}

function AllocRow({ a, months, today, todayPos, horizonEnd }) {
  const N = months.length;
  const color = projectColor(a.project_id);
  const span = barSpan(a, months, today, horizonEnd);
  const endTxt = a.permanent ? "permanent" : (a.end_date ? `până la ${fmtDate(a.end_date)}` : "fără termen");
  const endCls = span.state === "ending" ? "text-amber-600 font-medium"
    : span.state === "ended" ? "text-slate-400 line-through" : "text-slate-500";
  return (
    <tr data-testid={`etl-alloc-${a.assignment_id}`}>
      <td className="!bg-white sticky left-0 z-[1] border-r border-slate-100 !py-1.5">
        <div className="pl-7">
          <div className="text-[13px] font-medium text-slate-800 leading-tight truncate" title={a.name}>{a.name}</div>
          <div className="text-[11px] leading-tight flex gap-1.5 items-center min-w-0">
            <span className="font-mono text-slate-400 shrink-0">{a.code}</span>
            {a.pct != null && <span className="text-brand shrink-0">· {a.pct}%</span>}
            <span className={`shrink-0 ${endCls}`}>· {endTxt}</span>
          </div>
        </div>
      </td>
      <td colSpan={N} className="!p-0 relative">
        <Track N={N} todayPos={todayPos}>
          {span.out ? (
            <div className="absolute inset-y-0 left-3 flex items-center text-[11px] text-slate-400 italic">{span.reason}</div>
          ) : (
            <Bar span={span} m={a} color={color} />
          )}
        </Track>
      </td>
    </tr>
  );
}

export function TimelineLegend() {
  const items = [
    { c: "#3d68b1", t: "alocare activă" },
    { c: "#d97706", t: "se eliberează în interval" },
    { c: "#94a3b8", t: "alocare încheiată" },
    { c: "#3d68b1", t: "permanentă / viitoare", dashed: true },
  ];
  return (
    <div className="flex items-center gap-4 text-[11px] text-slate-500 flex-wrap" data-testid="timeline-legend">
      {items.map((it) => (
        <span key={it.t} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-5 rounded-sm" style={{ background: it.c, opacity: it.dashed ? 0.6 : 1, border: it.dashed ? "1px dashed #fff" : "none" }} />
          {it.t}
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5"><span className="h-3 w-px bg-rose-400" /> azi</span>
    </div>
  );
}
